import raw35Lessons from "../../../docs/3-5-lessons.json";
import type { MindUPLesson } from "./k-2";
import {
  buildLessonSourceText,
  chooseFirstNonEmpty,
  cleanCurriculumText,
  deriveStructuredSections,
  isLikelyContaminatedList,
  normalizeActivities,
  sanitizeBrainLinkText,
  sanitizePortfolioText,
  sanitizePurposeText,
  splitLiteratureItems,
  splitListItems,
} from "./normalize-raw";

type RawLesson = Partial<MindUPLesson> & {
  id?: string;
  content?: Record<string, unknown>;
};

function normalizeLesson(rawLesson: RawLesson): MindUPLesson {
  const content = rawLesson.content ?? {};
  const maybeFollowingUp = (content.followingUp ?? {}) as Record<string, unknown>;
  const sourceText = buildLessonSourceText(rawLesson);
  const sections = deriveStructuredSections(sourceText);
  const rawConcepts = splitListItems(rawLesson.concepts);
  const rawObjectives = splitListItems(rawLesson.objectives);
  const rawSel = splitListItems(rawLesson.socialEmotionalLearning);
  const rawMaterials = splitListItems(rawLesson.materials);

  const unit = typeof rawLesson.unit === "number" ? rawLesson.unit : 1;
  const lessonNumber = typeof rawLesson.lessonNumber === "number" ? rawLesson.lessonNumber : 1;
  const id = cleanCurriculumText(rawLesson.id) || `g3-5-u${unit}-l${lessonNumber}`;

  return {
    id,
    unit,
    unitTitle: cleanCurriculumText(rawLesson.unitTitle) || `Unit ${unit}`,
    lessonNumber,
    gradeLevel: "3-5",
    title: cleanCurriculumText(rawLesson.title) || `Lesson ${lessonNumber}`,
    purpose: sanitizePurposeText(rawLesson.purpose),
    concepts: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawConcepts) ? [] : rawConcepts,
      sections.concepts,
    ) ?? [],
    objectives: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawObjectives) ? [] : rawObjectives,
      sections.objectives,
    ) ?? [],
    socialEmotionalLearning: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawSel) ? [] : rawSel,
      sections.socialEmotionalLearning,
    ) ?? [],
    materials: chooseFirstNonEmpty(
      isLikelyContaminatedList(rawMaterials) ? [] : rawMaterials,
      sections.materials,
    ) ?? [],
    estimatedTime: cleanCurriculumText(rawLesson.estimatedTime) || "30 minutes",
    content: {
      brainLink:
        chooseFirstNonEmpty(
          sanitizeBrainLinkText(content.brainLink),
          sanitizeBrainLinkText(sections.brainLink),
        ) ?? "",
      gettingStarted: chooseFirstNonEmpty(splitListItems(content.gettingStarted), sections.gettingStarted) ?? [],
      activities: normalizeActivities(content.activities, cleanCurriculumText(rawLesson.title)),
      makingConnections: chooseFirstNonEmpty(splitListItems(content.makingConnections), sections.makingConnections) ?? [],
      followingUp: {
        mindupForHomeAndLife:
          chooseFirstNonEmpty(splitListItems(maybeFollowingUp.mindupForHomeAndLife), sections.mindupForHomeAndLife) ?? [],
        mindfulMePortfolio:
          chooseFirstNonEmpty(
            sanitizePortfolioText(maybeFollowingUp.mindfulMePortfolio),
            sanitizePortfolioText(sections.mindfulMePortfolio),
          ) ?? "",
      },
      simplificationIdeas: chooseFirstNonEmpty(splitListItems(content.simplificationIdeas), sections.simplificationIdeas) ?? [],
      challengerIdeas: chooseFirstNonEmpty(splitListItems(content.challengerIdeas), sections.challengerIdeas) ?? [],
      literatureList:
        chooseFirstNonEmpty(
          splitLiteratureItems(content.literatureList),
          sections.literatureList,
        ) ?? [],
    },
  };
}

export const G35_CURRICULUM: MindUPLesson[] = (raw35Lessons as RawLesson[]).map(normalizeLesson);

export function get35LessonById(id: string): MindUPLesson | undefined {
  return G35_CURRICULUM.find((lesson) => lesson.id === id);
}

export function get35LessonsByUnit(unit: number): MindUPLesson[] {
  return G35_CURRICULUM.filter((lesson) => lesson.unit === unit);
}
